import {
    AnyAction,
    createSlice,
    PayloadAction,
    Reducer,
} from '@reduxjs/toolkit';

import {
    EmploymentDetails,
    EmploymentsEndpoints,
    EmploymentsRefData,
    EmploymentsState,
    ValidationResponse,
} from './employmentsTypes';
import { initialStateEmployments as initialState } from './employmentsStateDefaultValue';
import { ApiStatus } from '../../types';

const employmentsSlice = createSlice({
    name: 'employments',
    initialState,
    reducers: {
        openEmploymentAddMode: (state) => {
            state.isAddMode = true;
        },

        openEmploymentEditMode: (state, action: PayloadAction<number>) => {
            state.employments[action.payload] = {
                ...state.employments[action.payload],
                recordNumber: action.payload,
                isEditMode: true,
            };
        },

        cancelEmploymentAddMode: (state) => {
            state.isAddMode = false;
            state.lastVisitedItem = {
                id: null,
                mode: 'add',
                isSaved: false,
            };  
        },

        cancelEmploymentEditMode: (state, action: PayloadAction<number>) => {
            const recordNumber = action.payload;

            if (recordNumber === undefined) return;

            state.employments[recordNumber].isEditMode = false;
            state.lastVisitedItem = {
                id: recordNumber,
                mode: 'edit',
                isSaved: false,
            };
        },

        getEmployments(state) {
            state.getEmploymentsApiStatus = ApiStatus.Pending;
        },
        getEmploymentsFulfilled(state, action: PayloadAction<Record<number, EmploymentDetails>>) {
            state.employments = action.payload;
            state.getEmploymentsApiStatus = ApiStatus.Fulfilled;
        },
        getEmploymentsRejected(state) {
            state.getEmploymentsApiStatus = ApiStatus.Rejected;
        },
        getEmploymentsRefData(state) {
            state.getRefDataApiStatus = ApiStatus.Pending;
        },
        getEmploymentsRefDataFulfilled(state, action: PayloadAction<EmploymentsRefData>) {
            const { content } = action.payload;

            state.content = JSON.parse(content);
            state.refData = action.payload;
            state.getRefDataApiStatus = ApiStatus.Fulfilled;
        },
        getEmploymentsRefDataRejected(state) {
            state.getRefDataApiStatus = ApiStatus.Rejected;
        },
        addEmployment(state, action: PayloadAction<EmploymentDetails>) {
            state.addEmploymentApiStatus = ApiStatus.Pending;
        },
        addEmploymentFulfilled(state, action: PayloadAction<EmploymentDetails>) {
            const { employmentId } = action.payload;

            const lastIndex = Object.keys(state.employments).length;

            state.employments[lastIndex] = action.payload;
            state.isAddMode = false;
            state.lastVisitedItem = {
                employerId: employmentId,
                id: lastIndex,
                mode: 'add',
                isSaved: true,
            };
            state.addEmploymentApiStatus = ApiStatus.Fulfilled;
        },
        addEmploymentRejected(state) {
            state.addEmploymentApiStatus = ApiStatus.Rejected;
        },
        updateEmployment(state, action: PayloadAction<EmploymentDetails>) {
            state.updateEmploymentApiStatus = ApiStatus.Pending;
        },
        updateEmploymentFulfilled(state, action: PayloadAction<EmploymentDetails>) {
            const { employmentId, recordNumber } = action.payload;

            if (recordNumber === undefined) return;

            state.employments[recordNumber] = action.payload;
            state.employments[recordNumber].isEditMode = false;
            state.lastVisitedItem = {
                employerId: employmentId,
                id: recordNumber,
                mode: 'edit',
                isSaved: true,
            };
            state.updateEmploymentApiStatus = ApiStatus.Fulfilled;
        },
        updateEmploymentRejected(state) {
            state.updateEmploymentApiStatus = ApiStatus.Rejected;
        },
        validateEmployments: (state, action: PayloadAction<Record<number, EmploymentDetails>>) => {
            state.validateApiStatus = ApiStatus.Pending;
        },
        validateEmploymentsFulfilled(state, action: PayloadAction<ValidationResponse>) {
            state.validationResponse = action.payload;
            state.validateApiStatus = ApiStatus.Fulfilled;
        },
        validateEmploymentsRejected: (state) => {
            state.validateApiStatus = ApiStatus.Rejected;
        },
        submitEmployments(state) {
            state.submitApiStatus = ApiStatus.Idle;
        },
        submitEmploymentsFulfilled(state) {
            state.submitApiStatus = ApiStatus.Fulfilled;
        },
        submitEmploymentsRejected(state) {
            state.submitApiStatus = ApiStatus.Rejected;
        },
        updateEmploymentEndpoints(state, action: PayloadAction<EmploymentsEndpoints>) {
            state.apiEndpoints = action.payload;
        },
        resetEmploymentValidations(state) {
            state.validationResponse = {
                result: false,
                validationMessageHeading: '',
                validationMessageDescription: '',
                showConfirmation: false,
                confirmationMessage: '',
            };
        },
        resetEmploymentStatus(state) {
            state.getEmploymentsApiStatus = ApiStatus.Idle;
            state.getRefDataApiStatus = ApiStatus.Idle;
            state.addEmploymentApiStatus = ApiStatus.Idle;
            state.updateEmploymentApiStatus = ApiStatus.Idle;
            state.validateApiStatus = ApiStatus.Idle;
            state.submitApiStatus = ApiStatus.Idle;
        },
        resetEmploymentLastVisited(state) {
            state.lastVisitedItem = {
                id: null,
                mode: null,
                isSaved: false,
            };
        },
    },
});

export const {
    openEmploymentAddMode,
    openEmploymentEditMode,
    cancelEmploymentAddMode,
    cancelEmploymentEditMode,
    getEmployments,
    getEmploymentsFulfilled,
    getEmploymentsRejected,
    getEmploymentsRefData,
    getEmploymentsRefDataFulfilled,
    getEmploymentsRefDataRejected,
    addEmployment,
    addEmploymentFulfilled,
    addEmploymentRejected,
    updateEmployment,
    updateEmploymentFulfilled,
    updateEmploymentRejected,
    validateEmployments,
    validateEmploymentsFulfilled,
    validateEmploymentsRejected,
    submitEmployments,
    submitEmploymentsFulfilled,
    submitEmploymentsRejected,
    updateEmploymentEndpoints,
    resetEmploymentValidations,
    resetEmploymentStatus,
    resetEmploymentLastVisited,
} = employmentsSlice.actions;

export const employmentsReducer: Reducer<EmploymentsState, AnyAction> = employmentsSlice.reducer;
